import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { useState, useEffect } from 'react';
import {
  Avatar,
  Box, Fade, IconButton, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography,
} from '@mui/material';
import { getQueryStringValue } from 'utils';
import { Link, useHistory } from 'react-router-dom';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { triggerAnalyticEvent } from 'utils/analytics';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import useDebouncedEffect from 'use-debounced-effect';
import HistoryIcon from '@mui/icons-material/History';
import Skeleton from 'components/Skeleton';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import SearchInput from './SearchInput';
import useFetchedOptions from './useFetchedOptions';
import { Groups } from './constants';
import useSearchHistory from './useSearchHistory';

export default function SearchOverlay({
  onChange, onSelect, backdrop, closeOnBlur = true, ...props
}) {
  const { query } = useParams();
  const [value, setValue] = useState(query || getQueryStringValue('q') || '');
  const [open, setOpen] = useState(false);
  const { options: fetchedOptions, loading, count } = useFetchedOptions(value);
  const searchHistory = useSearchHistory();
  const history = useHistory();

  const options = React.useMemo(() => {
    if (!open) {
      return [];
    }
    if (loading) {
      return [{ label: Skeleton.props.string() }, { label: Skeleton.props.string() }];
    }
    if (!value || fetchedOptions.length === 0) {
      return searchHistory.records.map((r, index) => ({
        label: r.text,
        group: Groups.HISTORY,
        id: `search-history-${index}`,
      }));
    }
    return fetchedOptions;
  }, [value, fetchedOptions, loading, open, searchHistory.records]);

  useDebouncedEffect(() => {
    if (!value || value.length < 3 || fetchedOptions.length === 0) {
      return;
    }
    searchHistory.add(value);
  }, 2000, [fetchedOptions, value]);

  useEffect(() => {
    if (!value) {
      return;
    }
    triggerAnalyticEvent('search', {
      search_term: value,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedOptions]);

  function handleChange({ target }) {
    setValue(target.value);
  }

  function handleSelect(e, option) {
    if (e.defaultPrevented) {
      return;
    }
    if (typeof option !== 'object') {
      return;
    }
    if (Groups.HISTORY === option.group) {
      setValue(option.label);
      return;
    }
    if (onSelect) {
      onSelect(option);
    }
    if (Groups.ITEMS === option.group) {
      history.push(`/place/${option.restaurant}/item/${option.id}`);
    } else {
      history.push(`/place/${option.id}/${option.slug || ''}`);
    }
    setOpen(false);
  }

  function renderOption(props, {
    group, label, image, description,
  }) {
    const item = (
      <ListItem
        {...props}
        secondaryAction={group === Groups.HISTORY && (
          <IconButton
            edge="end"
            aria-label="delete"
            onClick={(e) => {
              e.preventDefault();
              searchHistory.remove(label);
            }}
            sx={{ opacity: 0.6 }}
          >
            <ClearRoundedIcon />
          </IconButton>
        )}
      >
        <ListItemAvatar>
          <Avatar alt={label} src={image}>
            {
              group === Groups.HISTORY && !loading ?
                <HistoryIcon /> :
                <RestaurantIcon />
            }
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={label} secondary={description} />
      </ListItem>
    );
    return loading ?
      <Skeleton>{item}</Skeleton> : item;
  }

  function renderGroup(params) {
    return (
      <div key={`group-${params.key}`}>
        <Stack px={2} py={1} component="li" key={params.key} direction="row" justifyContent="space-between">
          <Typography textTransform="capitalize" fontWeight="bold">{params.group}</Typography>
          {
            params.group !== Groups.HISTORY && !loading && (
              <Typography
                variant="subtitle2"
                component={Link}
                to={`/${params.group}/${value}`}
                sx={{ textDecoration: 'none', color: 'primary.main' }}
                onClick={onSelect}
              >
                See all (
                {count[params.group]}
                )
              </Typography>
            )
          }

        </Stack>
        <List disablePadding dense>
          {params.children}
        </List>
      </div>
    );
  }

  return (
    <>
      {
        backdrop && (
          <Fade in={open}>
            <Box
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                backgroundColor: 'rgba(0, 0, 0,0.5)',
              }}
              onClick={() => setOpen(false)}
            />
          </Fade>
        )
      }
      <Autocomplete
        // key={options.length}
        freeSolo
        open={closeOnBlur ? open && options.length > 0 : open || options.length > 0}
        getOptionKey={(o) => o.id || o.label}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        filterOptions={(o) => o}
        options={options}
        loading={loading}
        onChange={handleSelect}
        groupBy={(option) => option.group}
        renderGroup={renderGroup}
        renderOption={renderOption}
        disableClearable
        disableCloseOnSelect
        disablePortal
        ListboxProps={{
          sx: {
            maxHeight: '70vh',
          },
        }}
        renderInput={(params) => (
          <SearchInput
            {...params}
            inputProps={{
              ...params.inputProps,
              value,
            }}
            value={value}
            onChange={handleChange}
            loading={loading}
          />
        )}
        forcePopupIcon={false}
        {...props}
      />
    </>

  );
}
